// ===================================================================
// SubscriptionModal.tsx - VERSION CORRIGÉE
// Correction du bug de disparition du bouton de réabonnement
// ===================================================================

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Browser } from '@capacitor/browser';
import paymentService from '../services/paymentService';
import stripeConfig, { validateStripeConfig } from '../config/stripe';
import subscriptionService from '../services/subscriptionService';
import revenueCatService from '../services/revenueCatService';
import { supabase } from '../services/supabaseAudioService';
import secureAuthService from '../services/secureAuthService';
import { useTheme } from './ThemeContext';

import { EmojiText, Picto } from './common/Picto';
interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userEmail?: string;
  blockedFeature?: string;
  context?: 'post_session' | 'streak_milestone' | 'manual';
  streakCount?: number;
  currentSubscription?: {
    status: string;
    tier: string;
    planId?: string;
    billingPeriod?: string;
    autoRenew?: boolean;
    expiresAt?: string;
    isAdmin?: boolean;
    cancelledAt?: string | null;
  } | null;
}

// ── Mapping feature → clé de traduction affichée dans le modal ──────────────
const BLOCKED_FEATURE_KEYS: Record<string, string> = {
  grammar_full:          'subscription.blockedFeatures.grammar_full',
  sentence_construction: 'subscription.blockedFeatures.sentence_construction',
  sentence_gap:          'subscription.blockedFeatures.sentence_gap',
  exercise_mode:         'subscription.blockedFeatures.exercise_mode',
  exercise_unlimited:    'subscription.blockedFeatures.exercise_unlimited',
  exercise_basic:        'subscription.blockedFeatures.exercise_basic',
  categories_full:       'subscription.blockedFeatures.categories_full',
  revision_unlimited:    'subscription.blockedFeatures.revision_unlimited',
  progress_stats:        'subscription.blockedFeatures.progress_stats',
  progress_stats_basic:  'subscription.blockedFeatures.progress_stats_basic',
  forge_game:            'subscription.blockedFeatures.forge_game',
  weaver_game:           'subscription.blockedFeatures.weaver_game',
};

// ── Bénéfices Premium affichés dans la liste ─────────────────────────────────
const PREMIUM_FEATURES = [
  { icon: '📚', key: 'grammar' },
  { icon: '🔤', key: 'construction' },
  { icon: '✍️', key: 'exercises' },
  { icon: '🔥', key: 'forgeWeaver' },
  { icon: '🎮', key: 'minigames' },
  { icon: '📂', key: 'vocabulary' },
  { icon: '🚫', key: 'noAds' },
] as const;

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userEmail,
  blockedFeature,
  context = 'manual',
  streakCount,
  currentSubscription
}) => {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const dark = theme === 'dark';

  const T = {
    modalBg:       dark ? '#2E1A0A' : '#F2E6CB',
    cardBg:        dark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.45)',
    cardBgSel:     dark ? 'rgba(200,112,64,0.22)' : 'rgba(200,112,64,0.12)',
    border:        dark ? 'rgba(200,112,64,0.22)' : 'rgba(200,112,64,0.22)',
    borderSel:     'var(--gold-primary)',
    title:         dark ? 'var(--gold-light)' : 'var(--gold-primary)',
    text:          dark ? '#F2E6CB' : '#2c1810',
    textMuted:     dark ? 'rgba(242,230,203,0.55)' : 'rgba(44,24,16,0.55)',
    infoBg:        dark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.45)',
    infoBorder:    dark ? 'rgba(200,112,64,0.2)' : 'rgba(200,112,64,0.25)',
    closeBg:       dark ? 'rgba(200,112,64,0.1)' : 'rgba(200,112,64,0.08)',
    closeBorder:   dark ? 'rgba(200,112,64,0.25)' : 'rgba(200,112,64,0.2)',
    closeText:     dark ? 'var(--gold-light)' : 'var(--gold-primary)',
    legalText:     dark ? 'rgba(242,230,203,0.4)' : 'rgba(44,24,16,0.45)',
  };

  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('yearly');
  // ✅ Prix localisés Google Play (Android natif) — remplacent les libellés
  // "2€"/"20€" codés en dur ci-dessous quand disponibles.
  const [nativeMonthlyPrice, setNativeMonthlyPrice] = useState<string | null>(null);
  const [nativeYearlyPrice, setNativeYearlyPrice] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);


  // Lire isAdmin depuis le JWT via secureAuthService (non falsifiable côté UI)
  const isAdminFromCache = React.useMemo(() => {
    return secureAuthService.isCurrentUserAdmin();
  }, [isOpen]);

  const subscription = React.useMemo(() => {
    if (currentSubscription && currentSubscription.tier) {
      // Enrichir avec isAdmin du cache si absent
      return {
        ...currentSubscription,
        isAdmin: currentSubscription.isAdmin === true || isAdminFromCache
      };
    }

    // Fallback : subscriptionService
    const planInfo = subscriptionService.getCurrentPlanInfo();
    const tier = subscriptionService.getCurrentTier();

    if (tier === 'premium' || isAdminFromCache) {
      const expiresAt = planInfo.expiresAt;
      return {
        tier: 'premium',
        status: 'active',
        planId: isAdminFromCache ? 'admin' : (planInfo.planId || 'premium_monthly'),
        billingPeriod: planInfo.billingPeriod || 'monthly',
        autoRenew: true,
        expiresAt: expiresAt ? expiresAt.toISOString() : null,
        isAdmin: isAdminFromCache,
        isPremium: true,
        cancelledAt: null as string | null
      };
    }

    return null;
  }, [currentSubscription, isAdminFromCache]);


  const isAdminUser = subscription?.isAdmin === true || subscription?.planId?.includes('admin');

  // Vérifier si l'utilisateur a un abonnement actif annulable (jamais un admin)
  const hasActiveSubscription = subscription?.status === 'active' &&
                                 subscription?.tier === 'premium' &&
                                 !isAdminUser;
  
  // ✅ Détecter l'annulation via cancelled_at
  const isSubscriptionCancelled = subscription?.cancelledAt !== undefined && 
                                   subscription?.cancelledAt !== null &&
                                   subscription?.cancelledAt !== '';
  
 
  
  const isPremiumUser = subscription?.tier === 'premium' || 
                        subscription?.isAdmin === true;

  // ✅ Initialisation au montage
  useEffect(() => {
    if (!isOpen) return;



    // ✅ Validation de la configuration
    const validation = validateStripeConfig();
    
    if (!validation.isValid) {
setConfigError(validation.errors.join('\n'));
      return;
    }

    
    // ✅ Initialiser le service de paiement seulement si pas déjÃ  premium
    if (!isPremiumUser) {
      const initPaymentService = async () => {
        try {
          await paymentService.initialize();
          setIsInitialized(true);
        } catch (error) {
setConfigError(t('subscription.errors.paymentInitFailed'));
        }
      };

      initPaymentService();
    }

    // ✅ Android natif : récupérer les prix localisés Google Play plutôt que
    // d'initialiser Stripe pour l'achat (Stripe reste utilisé pour l'annulation
    // legacy uniquement, voir les gardes MANAGE_VIA_PLAY_STORE ci-dessus).
    if (!isPremiumUser && revenueCatService.isAndroidNative()) {
      revenueCatService.getOfferings().then(({ monthly, yearly }) => {
        setNativeMonthlyPrice(monthly?.product.priceString || null);
        setNativeYearlyPrice(yearly?.product.priceString || null);
      });
    }
  }, [isOpen, isPremiumUser, subscription]);

  // ✅ Réinitialiser les erreurs lors du changement de plan
  useEffect(() => {
    setError(null);
  }, [selectedPlan]);

  // ✅ FONCTION CORRIGÉE: Gestion de l'annulation d'abonnement
  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    setError(null);

    try {
     

      // ✅ Récupérer le token ET l'email depuis Supabase
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const email = session?.user?.email;
      
      if (!token) {
        throw new Error(t('subscription.errors.notAuthenticated'));
      }

      if (!email) {
        throw new Error(t('subscription.errors.emailNotFound'));
      }

      

      // ✅ Déterminer l'URL du backend (utilise la même config que App.tsx)
      const BACKEND_URL = process.env.REACT_APP_API_URL || 
                          'https://jogolinga-backend-production.up.railway.app';

   

      // Appel Ã  l'API backend pour annuler l'abonnement
      const response = await fetch(`${BACKEND_URL}/api/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();

        // ✅ Abonnement souscrit via Google Play : pas d'API de gestion tierce,
        // on redirige vers le centre d'abonnements du Play Store.
        if (errorData.error === 'MANAGE_VIA_PLAY_STORE' && errorData.manageUrl) {
          setIsCancelling(false);
          setShowCancelConfirm(false);
          await Browser.open({ url: errorData.manageUrl });
          onClose();
          return;
        }

        throw new Error(errorData.error || t('subscription.errors.cancelError'));
      }

      const result = await response.json();


      // Fermer la confirmation et le modal
      setShowCancelConfirm(false);

      // Notifier le succès
      alert(t('subscription.cancelSuccess'));
      
      // Appeler onSuccess pour rafraîchir les données
      onSuccess();
      
      // Fermer le modal
      onClose();

    } catch (error) {
setIsCancelling(false);
      
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError(t('subscription.errors.cancelRetry'));
      }

      setShowCancelConfirm(false);
    }
  };

  // ✅ FONCTION CORRIGÉE : Reprendre l'abonnement
  const handleResumeSubscription = async () => {
   
    setIsResuming(true);
    setError(null);

    try {
      // Récupérer le token depuis Supabase
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const userEmail = session?.user?.email;
      
      if (!token) {
        throw new Error(t('subscription.errors.notAuthenticated'));
      }

      const BACKEND_URL = process.env.REACT_APP_API_URL ||
                          'https://jogolinga-backend-production.up.railway.app';



      const response = await fetch(`${BACKEND_URL}/api/subscription/resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();

        // ✅ Abonnement souscrit via Google Play : pas d'API de gestion tierce,
        // on redirige vers le centre d'abonnements du Play Store.
        if (errorData.error === 'MANAGE_VIA_PLAY_STORE' && errorData.manageUrl) {
          setIsResuming(false);
          await Browser.open({ url: errorData.manageUrl });
          onClose();
          return;
        }

        throw new Error(errorData.error || t('subscription.errors.resumeError'));
      }

      const result = await response.json();
      

      // ✅ CORRECTION 1: Recharger le service d'abonnement
      subscriptionService.reloadSubscription();
      
      // ✅ CORRECTION 2: Nettoyer le cache localStorage pour forcer le refresh
      if (userEmail) {
        // Supprimer l'ancien cache
        localStorage.removeItem('user_subscription');
        
        // Créer un nouveau cache avec les données Ã  jour
        localStorage.setItem('user_subscription', JSON.stringify({
          email: userEmail,
          tier: 'premium',
          status: 'active',
          verified: true,
          verifiedBy: 'resume_subscription',
          cachedAt: Date.now(),
          cancelledAt: null // ✅ IMPORTANT: Réinitialiser cancelledAt
        }));
        
      
      }
      
      // ✅ CORRECTION 3: Dispatcher un événement pour rafraîchir App.tsx
      window.dispatchEvent(new CustomEvent('subscriptionUpdated', {
        detail: { 
          tier: 'premium',
          isPremium: true,
          status: 'active',
          cancelledAt: null // ✅ Préciser que l'abonnement n'est plus annulé
        }
      }));
    
      
      // ✅ CORRECTION 4: Attendre un peu que tout se propage
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Notifier le succès
      alert(t('subscription.resumeSuccess'));
      
      // ✅ CORRECTION 5: Appeler onSuccess AVANT de fermer pour rafraîchir les données
      await onSuccess();
      
    
      
      // ✅ CORRECTION 6: Attendre encore un peu avant de fermer
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Fermer le modal
      onClose();

    } catch (error) {
if (error instanceof Error) {
        setError(error.message);
      } else {
        setError(t('subscription.errors.resumeRetry'));
      }
    } finally {
      setIsResuming(false);
    }
  };

  // ✅ Gestion du clic sur "Sélectionner"
  const handleSubscribe = async () => {

    // ✅ Vérifications préalables
    if (!userEmail) {
setError(t('subscription.errors.pleaseLogin'));
      return;
    }

    setIsLoading(true);
    setError(null);

    // ✅ Android natif : Google Play Billing via RevenueCat — Stripe reste
    // réservé au web (le règlement Google Play impose sa propre facturation
    // pour le contenu numérique consommé dans l'app).
    if (revenueCatService.isAndroidNative()) {
      try {
        // ✅ Utilise la session Supabase (source de vérité pour le flux Google
        // OAuth actif, cf. GoogleAuth.tsx) plutôt que secureAuthService, qui
        // n'est peuplé que par l'ancien flux de connexion JWT.
        const { data: { session } } = await supabase.auth.getSession();
        const currentUserId = session?.user?.id;
        if (currentUserId) {
          await revenueCatService.configure(currentUserId, userEmail);
        }

        const offerings = await revenueCatService.getOfferings();
        const pkg = selectedPlan === 'yearly' ? offerings.yearly : offerings.monthly;

        if (!pkg) {
          setError(t('subscription.errors.offeringUnavailable'));
          setIsLoading(false);
          return;
        }

        const purchased = await revenueCatService.purchasePackage(pkg);
        if (purchased) {
          onSuccess();
          onClose();
        }
        // purchased === false : l'utilisateur a fermé la fenêtre d'achat
        // native, ce n'est pas une erreur à afficher.
      } catch (error) {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError(t('subscription.errors.genericRetry'));
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Web : chemin Stripe existant, inchangé.
    if (!isInitialized || !paymentService.isReady()) {
setError(t('subscription.errors.paymentNotReady'));
      setIsLoading(false);
      return;
    }

    try {
      // ✅ Initier le paiement (création session + redirection)
      await paymentService.initiatePayment({
        priceId: stripeConfig.prices[selectedPlan],
        customerEmail: userEmail,
        plan: selectedPlan
      });

    } catch (error) {
if (error instanceof Error) {
        setError(error.message);
      } else {
        setError(t('subscription.errors.genericRetry'));
      }

      setIsLoading(false);
    }
  };

  // ✅ Restauration des achats Google Play — requis par Google (réinstallation,
  // changement d'appareil). N'a de sens que sur Android natif.
  const handleRestorePurchases = async () => {
    setIsRestoring(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        await revenueCatService.configure(session.user.id, userEmail || session.user.email || null);
      }

      const restored = await revenueCatService.restorePurchases();
      if (restored) {
        onSuccess();
        onClose();
      } else {
        setError(t('subscription.errors.noPurchasesToRestore'));
      }
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError(t('subscription.errors.genericRetry'));
      }
    } finally {
      setIsRestoring(false);
    }
  };

  // ✅ Gestion de la fermeture
  const handleClose = () => {
    if (!isLoading && !isCancelling && !isResuming && !isRestoring) {
      onClose();
    }
  };

  // ✅ Ne rien afficher si le modal n'est pas ouvert
  if (!isOpen) return null;

  // ✅ Affichage erreur de configuration
  if (configError) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '20px'
        }}
        onClick={handleClose}
      >
        <div
          style={{
            backgroundColor: T.modalBg,
            borderRadius: '18px',
            padding: '32px 28px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 12px 40px rgba(44,24,16,0.2)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '14px', color: '#c0392b', fontFamily: 'var(--font-serif, Fraunces, Georgia, serif)' }}>
            {t('subscription.configMissing')}
          </h2>
          <p style={{ color: T.text, opacity: 0.7, marginBottom: '22px', whiteSpace: 'pre-line', fontSize: '14px', lineHeight: '1.5' }}>
            {configError}
          </p>
          <button
            onClick={handleClose}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: T.closeBg,
              color: T.closeText,
              border: `1px solid ${T.closeBorder}`,
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600'
            }}
          >
            {t('subscription.close')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        padding: '20px',
        overflowY: 'auto'
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: T.modalBg,
          borderRadius: '20px',
          padding: '32px 28px',
          maxWidth: '560px',
          width: '100%',
          boxShadow: dark ? '0 12px 48px rgba(0,0,0,0.6)' : '0 12px 48px rgba(44,24,16,0.22)',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête contextuel */}
        <div style={{ textAlign: 'center', marginBottom: blockedFeature && !isPremiumUser ? '16px' : '24px' }}>
          <div style={{ fontSize: '44px', marginBottom: '10px' }}>
            <EmojiText text={isAdminUser ? '👑' : isPremiumUser ? '⭐' : context === 'post_session' ? '🚀' : context === 'streak_milestone' ? '🔥' : '✨'} />
          </div>
          <h2 style={{ fontSize: '26px', fontWeight: '700', marginBottom: '8px', color: T.title, fontFamily: 'var(--font-serif, Fraunces, Georgia, serif)' }}>
            {isAdminUser
              ? t('subscription.headers.admin')
              : isPremiumUser
              ? t('subscription.headers.premiumActive')
              : context === 'post_session'
              ? t('subscription.headers.postSession')
              : context === 'streak_milestone'
              ? t('subscription.headers.streakMilestone', { count: streakCount })
              : t('subscription.headers.default')}
          </h2>
          <p style={{ color: T.text, opacity: 0.65, fontSize: '14px', lineHeight: '1.5', margin: '0 0 10px' }}>
            {isAdminUser
              ? t('subscription.subheaders.admin')
              : isPremiumUser
              ? t('subscription.subheaders.premiumActive')
              : context === 'post_session'
              ? t('subscription.subheaders.postSession')
              : context === 'streak_milestone'
              ? t('subscription.subheaders.streakMilestone')
              : t('subscription.subheaders.default')}
          </p>
          {/* Social proof */}
          {!isPremiumUser && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: dark ? 'rgba(200,112,64,0.12)' : 'rgba(200,112,64,0.08)',
              border: '1px solid rgba(200,112,64,0.25)',
              borderRadius: '20px',
              padding: '4px 14px',
              fontSize: '12px',
              color: T.title,
              fontWeight: '600',
            }}>
              <span>𓆣</span> {t('subscription.socialProof')}
            </div>
          )}
        </div>

        {/* Bannière contextuelle — feature qui a déclenché le modal */}
        {!isPremiumUser && blockedFeature && BLOCKED_FEATURE_KEYS[blockedFeature] && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            backgroundColor: dark ? 'rgba(200,112,64,0.15)' : 'rgba(200,112,64,0.10)',
            border: '1px solid rgba(200,112,64,0.35)',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '24px',
          }}>
            <span style={{ fontSize: '20px', flexShrink: 0 }}><Picto name="cadenas" /></span>
            <p style={{ margin: 0, fontSize: '14px', color: T.text, lineHeight: '1.45' }}>
              <strong style={{ color: T.title }}>{t(BLOCKED_FEATURE_KEYS[blockedFeature])}</strong>
              {t('subscription.blockedFeatureSuffix')}
            </p>
          </div>
        )}

        {/* ✅ Section informations abonnement pour les premium */}
        {isPremiumUser && subscription && (
          <div style={{
            backgroundColor: T.infoBg,
            borderRadius: '14px',
            padding: '18px 20px',
            marginBottom: '22px',
            border: `1px solid ${T.infoBorder}`
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '700',
              marginBottom: '14px',
              color: T.text
            }}>
               {t('subscription.yourSubscription')}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: T.textMuted, fontWeight: '500', fontSize: '13px' }}>{t('subscription.plan')}</span>
                <span style={{ color: T.text, fontWeight: '700', fontSize: '13px' }}>
                  {isAdminUser ? t('subscription.adminAccess') : subscription.billingPeriod === 'yearly' ? t('subscription.premiumYearly') : t('subscription.premiumMonthly')}
                </span>
              </div>

              {subscription.expiresAt && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: T.textMuted, fontWeight: '500', fontSize: '13px' }}>{t('subscription.expiration')}</span>
                  <span style={{ color: T.text, fontWeight: '700', fontSize: '13px' }}>
                    {new Date(subscription.expiresAt).toLocaleDateString(i18n.language, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              )}

              {!isAdminUser && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: T.textMuted, fontWeight: '500', fontSize: '13px' }}>{t('subscription.nextRenewal')}</span>
                  <span style={{
                    color: isSubscriptionCancelled ? 'var(--color-error)' : 'var(--color-success)',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    {isSubscriptionCancelled ? (
                      <> {t('subscription.cancelled')}</>
                    ) : subscription.expiresAt ? (
                      <>
                        <Picto name="oui" /> {new Date(subscription.expiresAt).toLocaleDateString(i18n.language, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </>
                    ) : (
                      t('subscription.notAvailable')
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ✅ Liste des bénéfices Premium */}
        {!isPremiumUser && (
          <div style={{ marginBottom: '20px' }}>
            {PREMIUM_FEATURES.map((f, i) => (
              <div
                key={f.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 0',
                  borderBottom: i < PREMIUM_FEATURES.length - 1
                    ? `1px solid ${dark ? 'rgba(255,255,255,0.07)' : 'rgba(44,24,16,0.08)'}`
                    : 'none',
                }}
              >
                <span style={{ fontSize: '20px', width: '28px', textAlign: 'center', flexShrink: 0 }}>
                  <EmojiText text={f.icon} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: T.text }}>
                    {t(`subscription.features.${f.key}.label`)}
                  </div>
                  <div style={{ fontSize: '12px', color: T.textMuted, marginTop: '1px' }}>
                    {t(`subscription.features.${f.key}.desc`)}
                  </div>
                </div>
                <span style={{ fontSize: '16px', color: 'var(--color-success)', flexShrink: 0 }}>✓</span>
              </div>
            ))}
          </div>
        )}

        {/* ✅ Sélecteur de plan */}
        {!isPremiumUser && (
          <div style={{ marginBottom: '32px' }}>
            {/* Plans mensuel / annuel */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
              {/* Plan Mensuel */}
              <div
                onClick={() => setSelectedPlan('monthly')}
                style={{
                  flex: 1,
                  padding: '18px 16px',
                  border: selectedPlan === 'monthly' ? '1.5px solid var(--gold-primary)' : '1.5px solid rgba(200,112,64,0.22)',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: selectedPlan === 'monthly' ? T.cardBgSel : T.cardBg,
                  boxShadow: selectedPlan === 'monthly' ? '0 0 0 3px rgba(200,112,64,0.18)' : 'none'
                }}
              >
                <div style={{ fontSize: '17px', fontWeight: '700', marginBottom: '8px', color: T.text }}>
                  {t('subscription.monthly')}
                </div>
                <div style={{ fontSize: '30px', fontWeight: '800', color: 'var(--gold-primary)', lineHeight: 1.1 }}>
                  {nativeMonthlyPrice || '2€'}
                  <span style={{ fontSize: '14px', fontWeight: '400', color: T.textMuted }}>{t('subscription.perMonth')}</span>
                </div>
              </div>

              {/* Plan Annuel */}
              <div
                onClick={() => setSelectedPlan('yearly')}
                style={{
                  flex: 1,
                  padding: '18px 16px',
                  border: selectedPlan === 'yearly' ? '1.5px solid var(--gold-primary)' : '1.5px solid rgba(200,112,64,0.22)',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: selectedPlan === 'yearly' ? T.cardBgSel : T.cardBg,
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  boxShadow: selectedPlan === 'yearly' ? '0 0 0 3px rgba(200,112,64,0.18)' : 'none'
                }}
              >
                {/* Ribbon diagonal coin supérieur droit */}
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  right: '-30px',
                  backgroundColor: 'var(--color-success)',
                  color: 'white',
                  fontSize: '9px',
                  fontWeight: 'bold',
                  padding: '3px 36px',
                  transform: 'rotate(35deg)',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                  letterSpacing: '0.3px'
                }}>
                  {t('subscription.twoMonthsFree')}
                </div>

                {/* Titre */}
                <div style={{ fontSize: '17px', fontWeight: '700', color: T.text, marginBottom: '4px' }}>
                  {t('subscription.yearly')}
                </div>

                <div style={{ fontSize: '30px', fontWeight: '800', color: 'var(--gold-primary)', lineHeight: 1.1 }}>
                  {nativeYearlyPrice || '20€'}
                  <span style={{ fontSize: '14px', fontWeight: '400', color: T.textMuted }}>{t('subscription.perYear')}</span>
                </div>
                <div style={{ fontSize: '12px', color: '#2d9e6b', fontWeight: '600' }}>
                  {t('subscription.monthlyEquivalent')}
                </div>
              </div>
            </div>

            {/* ✅ Restaurer mes achats — Android natif uniquement, requis par Google */}
            {revenueCatService.isAndroidNative() && (
              <button
                onClick={handleRestorePurchases}
                disabled={isRestoring || isLoading}
                style={{
                  width: '100%',
                  marginTop: '12px',
                  padding: '8px',
                  background: 'none',
                  border: 'none',
                  color: T.textMuted,
                  fontSize: '13px',
                  textDecoration: 'underline',
                  cursor: isRestoring ? 'not-allowed' : 'pointer'
                }}
              >
                {isRestoring ? t('subscription.restoring') : t('subscription.restorePurchases')}
              </button>
            )}

          </div>
        )}

        {/* ✅ Messages d'erreur */}
        {error && (
          <div style={{
            backgroundColor: 'var(--color-error-tint)',
            border: '1px solid var(--color-error)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '20px' }}></span>
            <span style={{ color: 'var(--color-error-dark)', fontSize: '14px' }}>{error}</span>
          </div>
        )}

        {/* ✅ Bouton de paiement - Masqué si déjÃ  premium */}
        {!isPremiumUser && (
          <button
            onClick={handleSubscribe}
            disabled={isLoading || !isInitialized}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: isLoading || !isInitialized ? 'rgba(200,112,64,0.35)' : 'var(--gold-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '14px',
              fontSize: '17px',
              fontWeight: '700',
              cursor: isLoading || !isInitialized ? 'not-allowed' : 'pointer',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            {isLoading ? (
              <>
                <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}></span>
                {t('subscription.loadingPayment')}
              </>
            ) : (
              <>
                {t('subscription.continueToPayment')}
                <span>→</span>
              </>
            )}
          </button>
        )}

        {/* Garanties */}
        {!isPremiumUser && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            fontSize: '11px',
            color: T.textMuted,
            marginBottom: '12px',
          }}>
            <span>{t('subscription.cancelAnytime')}</span>
            <span>
              {revenueCatService.isAndroidNative()
                ? t('subscription.securePaymentGooglePlay')
                : t('subscription.securePayment')}
            </span>
          </div>
        )}

        {/* ✅ Bouton Fermer */}
        {!isLoading && !isCancelling && !isResuming && (
          <button
            onClick={handleClose}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: T.closeBg,
              color: T.closeText,
              border: `1px solid ${T.closeBorder}`,
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              marginBottom: (hasActiveSubscription || isSubscriptionCancelled) ? '12px' : '20px'
            }}
          >
            {t('subscription.close')}
          </button>
        )}

        {/* ✅ CORRIGÉ : Afficher les boutons pour les utilisateurs premium */}
        {isPremiumUser && !isLoading && !isCancelling && !isResuming && (
          <>
            {isSubscriptionCancelled ? (
              // ✅ Bouton "Reprendre l'abonnement" - visible si annulé
              <button
                onClick={handleResumeSubscription}
                disabled={isResuming}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: isResuming ? '#9ca3af' : 'var(--color-success-tint)',
                  color: isResuming ? '#6b7280' : 'var(--color-success-dark)',
                  border: '1px solid var(--color-success-light)',
                  borderRadius: '8px',
                  cursor: isResuming ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '20px',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (!isResuming) e.currentTarget.style.backgroundColor = 'var(--color-success-light)';
                }}
                onMouseLeave={(e) => {
                  if (!isResuming) e.currentTarget.style.backgroundColor = 'var(--color-success-tint)';
                }}
              >
                {isResuming ? (
                  <>
                    <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}></span>
                    {t('subscription.reactivating')}
                  </>
                ) : (
                  <>{t('subscription.resumeSubscription')}</>
                )}
              </button>
            ) : hasActiveSubscription ? (
              // ✅ Bouton "Annuler" - visible seulement si actif ET non-annulé
              <button
                onClick={() => setShowCancelConfirm(true)}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'var(--color-error-tint)',
                  color: 'var(--color-error)',
                  border: '1px solid var(--color-error)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '20px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-error)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-error-tint)';
                }}
              >
                <EmojiText text={t('subscription.cancelSubscription') as string} />
              </button>
            ) : null}
          </>
        )}

        {/* ✅ Informations légales */}
        <div style={{ textAlign: 'center', fontSize: '11px', color: T.legalText, lineHeight: '1.7' }}>
          <div style={{ marginBottom: '6px' }}>
            {revenueCatService.isAndroidNative()
              ? t('subscription.securePaymentFooterGooglePlay')
              : t('subscription.securePaymentFooter')}
          </div>
          <div>
            {t('subscription.termsAgreeBefore')}
            <a href="/terms" style={{ color: 'var(--gold-primary)', textDecoration: 'underline', opacity: 1 }}>
              {t('subscription.termsLink')}
            </a>
            {t('subscription.termsAgreeMiddle')}
            <a href="/privacy" style={{ color: 'var(--gold-primary)', textDecoration: 'underline', opacity: 1 }}>
              {t('subscription.privacyLink')}
            </a>
          </div>
        </div>
      </div>

      {/* ✅ MODALE: Confirmation d'annulation */}
      {showCancelConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={() => !isCancelling && setShowCancelConfirm(false)}
        >
          <div
            style={{
              backgroundColor: T.modalBg,
              borderRadius: '18px',
              padding: '32px 28px',
              maxWidth: '460px',
              width: '100%',
              boxShadow: '0 12px 48px rgba(0,0,0,0.35)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}><Picto name="tristesse" /></div>
              <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '12px', color: T.text, fontFamily: 'var(--font-serif, Fraunces, Georgia, serif)' }}>
                {t('subscription.cancelConfirm.title')}
              </h2>
              <p style={{ color: T.text, opacity: 0.65, lineHeight: '1.6', fontSize: '14px' }}>
                {t('subscription.cancelConfirm.textBefore')}
                {subscription?.billingPeriod === 'yearly' ? t('subscription.cancelConfirm.endOfYear') : t('subscription.cancelConfirm.endOfMonth')}
                {t('subscription.cancelConfirm.textAfter')}
              </p>
            </div>

            {error && (
              <div style={{
                backgroundColor: 'var(--color-error-tint)',
                border: '1px solid var(--color-error)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}></span>
                <span style={{ color: 'var(--color-error-dark)', fontSize: '14px' }}>{error}</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
              <button
                onClick={handleCancelSubscription}
                disabled={isCancelling}
                style={{
                  padding: '14px',
                  backgroundColor: isCancelling ? '#9ca3af' : 'var(--color-error)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: isCancelling ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {isCancelling ? (
                  <>
                    <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}></span>
                    {t('subscription.cancelConfirm.cancelling')}
                  </>
                ) : (
                  <>
                    <EmojiText text={t('subscription.cancelConfirm.confirmCancel') as string} />
                  </>
                )}
              </button>

              {!isCancelling && (
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  style={{
                    padding: '14px',
                    backgroundColor: T.closeBg,
                    color: T.closeText,
                    border: `1px solid ${T.closeBorder}`,
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {t('subscription.cancelConfirm.keepSubscription')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ✅ Animation de rotation pour le spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SubscriptionModal;